"use client";

import type { PlainDate } from "@sa-nu/shared-utils/date";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { SiteDetailDrawer } from "./site-detail-drawer";

/** Drawer を開くときに必要な情報。拠点詳細は経路が1つのみのため union 化しない。 */
export type SiteDetailDrawerPayload = {
  siteSlug: string;
  /** 拠点別カレンダーのセルタップ経由で開いた場合のタップ日。拠点名/画像タップ等は省略される */
  selectedNight?: PlainDate;
};

type SiteDetailDrawerActions = {
  open: (payload: SiteDetailDrawerPayload) => void;
  close: () => void;
};

const SiteDetailDrawerContext = createContext<SiteDetailDrawerActions | null>(
  null,
);

export function useSiteDetailDrawer(): SiteDetailDrawerActions {
  const ctx = useContext(SiteDetailDrawerContext);
  if (!ctx)
    throw new Error(
      "useSiteDetailDrawer must be used within SiteDetailDrawerProvider",
    );
  return ctx;
}

export function SiteDetailDrawerProvider({
  children,
  onDidClose,
}: {
  children: ReactNode;
  /** Drawer が閉じられた直後に呼ばれる。URL 同期など呼び出し側の関心事をここに閉じ込める。 */
  onDidClose?: () => void;
}) {
  const [payload, setPayload] = useState<SiteDetailDrawerPayload | null>(null);

  const close = useCallback(() => {
    setPayload(null);
    onDidClose?.();
  }, [onDidClose]);
  const open = useCallback((p: SiteDetailDrawerPayload) => setPayload(p), []);

  const actions = useMemo<SiteDetailDrawerActions>(
    () => ({ open, close }),
    [open, close],
  );

  return (
    <SiteDetailDrawerContext.Provider value={actions}>
      {children}
      <SiteDetailDrawer payload={payload} onClose={close} />
    </SiteDetailDrawerContext.Provider>
  );
}
